import { useIsMerchant } from "./use-is-merchant";
import { splitByReturnability } from "../lib/returnable-items.lib";
import { usePublicOrder } from "../queries/use-public-order";
import { useReturnRequests } from "../queries/use-return-requests";
import type { OrderLineItem } from "../types";

function useAllNonReturnableItems(
  orderId: string,
  shopSlug: string,
): OrderLineItem[] {
  const { data: order } = usePublicOrder(orderId);
  const { data: returnRequestsData } = useReturnRequests({ orderId, shopSlug });

  return splitByReturnability(
    order?.lineItems ?? [],
    returnRequestsData?.returnRequests ?? [],
  ).nonReturnable;
}

/**
 * Line items the shop excluded from returns, shown as disabled rows with a
 * badge in the article selection list. Empty for a merchant, who selects them
 * like any other article (see `useNonReturnableLineItemIds` for their marker).
 */
export function useNonReturnableItems(
  orderId: string,
  shopSlug: string,
): OrderLineItem[] {
  const items = useAllNonReturnableItems(orderId, shopSlug);
  const isMerchant = useIsMerchant();

  return isMerchant ? [] : items;
}

/**
 * Ids of the excluded line items, whoever is browsing. A merchant keeps those
 * articles selectable and only gets a marker on the row.
 */
export function useNonReturnableLineItemIds(
  orderId: string,
  shopSlug: string,
): Set<string> {
  const items = useAllNonReturnableItems(orderId, shopSlug);

  return new Set(items.map((item) => item.line_item_id));
}
